/**
 * Carbon select: the field frame around a native <select> drawn as a field,
 * with a 16px chevron 16px from the trailing edge. Pass `options`, or
 * <option> children for groups. For the inline "Workload: All" select in a
 * table toolbar, use TableSelect.
 */
import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react'
import clsx from 'clsx'
import { FormField } from './FormField'
import { FIELD_BASE, FIELD_HEIGHT, FIELD_INVALID, fieldIds } from './fieldStyles'
import { IconChevronDown } from './icons'
import type { FieldChromeProps } from './TextInput'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>, FieldChromeProps {
  options?: SelectOption[]
  wrapperClassName?: string
  children?: ReactNode
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, helper, error, hideLabel, size = 'md', id: idProp, className, wrapperClassName, options, children, disabled, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const { describedBy } = fieldIds(id, helper, error)
  return (
    <FormField id={id} label={label} helper={helper} error={error} hideLabel={hideLabel} disabled={disabled} className={wrapperClassName}>
      <div className="relative">
        <select
          ref={ref}
          id={id}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={clsx(
            FIELD_BASE,
            FIELD_HEIGHT[size],
            'cursor-pointer appearance-none pr-12 hover:bg-layer-hover disabled:hover:bg-field',
            '[&>option]:bg-layer-2 [&>option]:text-text-primary',
            error && FIELD_INVALID,
            className,
          )}
          {...rest}
        >
          {options
            ? options.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}
                </option>
              ))
            : children}
        </select>
        <IconChevronDown
          className={clsx(
            'pointer-events-none absolute right-4 top-1/2 -translate-y-1/2',
            disabled ? 'text-text-placeholder' : 'text-text-primary',
          )}
        />
      </div>
    </FormField>
  )
})

export default Select
